import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Bookmark, BookmarkCheck, Download, FileText, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ShareSheet } from "@/components/ShareSheet";
import { api } from "@/lib/apiClient";
import { isNative } from "@/lib/native";
import { useCurrency } from "@/context/CurrencyContext";
import { SynastrySnapshot, SynastryCharts, SynastrySections } from "@/components/SynastryView";

const WIDE = !isNative;
const DONE = ["ready", "failed", "none"];

function PdfBlock({ report, onBuy, buying }) {
  const { prices } = useCurrency();
  const [dl, setDl] = useState(false);
  const download = async () => {
    setDl(true);
    try {
      const { data } = await api.get(`/synastry/${report.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `Astra-Synastry-${report.a.name}-${report.b.name}.pdf`.replace(/\s+/g, "-"); a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Couldn't download the PDF. Please try again."); }
    setDl(false);
  };
  const s = report.pdf_status;
  return (
    <div className="bg-gradient-to-b from-[#1a1230] to-[#0F1523] border border-[#D4AF37]/40 rounded-2xl p-5" data-testid="synastry-pdf-block">
      <p className="text-[10px] uppercase tracking-[0.25em] gold-text mb-1"><FileText size={11} className="inline -mt-0.5 mr-1" />Illustrated PDF</p>
      <p className="text-lg font-light">Keep your reading forever</p>
      <p className="text-sm text-[#A7B0C0] mb-4">Both birth wheels, every planet contact, and five extra chapters: love languages, conflict and repair, closeness, your year together and rituals for two.</p>
      {s === "ready" ? (
        <button data-testid="btn-download-pdf" onClick={download} disabled={dl}
          className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium py-3 rounded-full transition-colors disabled:opacity-60">
          {dl ? <Loader2 size={18} className="animate-spin" /> : <><Download size={16} /> Download PDF</>}
        </button>
      ) : s === "generating" ? (
        <p className="text-sm gold-text flex items-center gap-2" data-testid="pdf-generating"><Loader2 size={14} className="animate-spin" /> Writing your extra chapters, about a minute…</p>
      ) : (
        <button data-testid="btn-buy-pdf" onClick={onBuy} disabled={buying}
          className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium py-3 rounded-full transition-colors disabled:opacity-60">
          {buying ? <Loader2 size={18} className="animate-spin" /> : `Get the PDF · ${prices.symbol}${prices.reading}`}
        </button>
      )}
    </div>
  );
}

function InviteBlock({ report }) {
  const [token, setToken] = useState(report.invite_token);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (report.partner_user_id) {
    return (
      <p data-testid="synastry-partner-joined" className="text-sm text-[#D6D0C5] bg-[#0F1523]/90 border border-[#D4AF37]/20 rounded-2xl p-4">
        <Heart size={14} className="inline gold-text mr-1.5 -mt-0.5" />{report.is_owner ? `${report.b.name} has joined. This reading now uses their own birth details.` : `You're sharing this reading with ${report.a.name}.`}
      </p>
    );
  }
  if (!report.is_owner) return null;
  const invite = async () => {
    setBusy(true);
    try {
      const { data } = token ? { data: { token } } : await api.post(`/synastry/${report.id}/invite`);
      setToken(data.token); setOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create the invite. Please try again."); }
    setBusy(false);
  };
  return (
    <>
      <button data-testid="btn-invite-partner" onClick={invite} disabled={busy}
        className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium py-3 rounded-full transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <><Heart size={16} /> Invite {report.b.name} to see it together</>}
      </button>
      {token && (
        <ShareSheet open={open} onClose={() => setOpen(false)} testId="invite-sheet" title={`Our Astra synastry reading`}
          desc={`${report.b.name} adds their own birth details and gets a free account, and the reading updates for you both.`}
          text={`I made us a synastry reading on Astra: we're "${report.analysis.archetype}". Add your birth details so it's exact and we can read it together:`}
          link={`${window.location.origin}/together/${token}`} />
      )}
    </>
  );
}

export default function SynastryReport() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { currency } = useCurrency();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);
  const [buying, setBuying] = useState(false);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    const sid = params.get("session_id");
    const load = async () => {
      try {
        const { data } = await api.get(`/synastry/${id}`);
        if (!alive) return;
        setReport(data);
        if ((data.status !== "ready" && data.status !== "failed") || !DONE.includes(data.pdf_status)) timer.current = setTimeout(load, 4000);
      } catch { if (alive) setError(true); }
    };
    (sid ? api.get(`/payments/status/${sid}`).catch(() => {}) : Promise.resolve()).then(load);
    return () => { alive = false; clearTimeout(timer.current); };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try { await api.post(`/synastry/${id}/save`); setReport({ ...report, saved: true }); toast.success("Saved to your archive"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save. Please try again."); }
    setSaving(false);
  };
  const buyPdf = async () => {
    setBuying(true);
    try {
      const { data } = await api.post(`/synastry/${id}/pdf-checkout`, { currency, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url; else setBuying(false);
    } catch { toast.error("Couldn't start checkout. Please try again."); setBuying(false); }
  };

  if (error) return <p data-testid="synastry-error" className="text-center text-[#A7B0C0] py-24 px-6">This reading couldn't be found. <Link to="/readings/synastry" className="gold-text">Start a new one</Link></p>;
  if (!report) return <div className="flex justify-center py-32"><Loader2 className="animate-spin gold-text" size={28} /></div>;

  const ready = report.status === "ready";
  return (
    <div data-testid="synastry-report-page" className={`px-5 pt-6 pb-10 fade-up ${WIDE ? "lg:max-w-6xl lg:mx-auto lg:px-10 lg:pt-10 lg:grid lg:grid-cols-[380px_1fr] lg:gap-10 lg:items-start" : ""}`}>
      <div className={`space-y-5 ${WIDE ? "lg:sticky lg:top-24" : ""}`}>
        <SynastrySnapshot report={report} />
        {ready && (
          <button data-testid="btn-save-synastry" onClick={save} disabled={saving || report.saved}
            className="w-full flex items-center justify-center gap-2 border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 gold-text py-3 rounded-full transition-colors disabled:opacity-70">
            {report.saved ? <><BookmarkCheck size={16} /> Saved to archive</> : <><Bookmark size={16} /> Save to archive</>}
          </button>
        )}
        {ready && <InviteBlock report={report} />}
      </div>
      <div className="space-y-5 mt-6 lg:mt-0">
        {!ready && (
          <div data-testid="synastry-waiting" className="bg-[#0F1523]/90 border border-[#D4AF37]/20 rounded-2xl p-6 text-center">
            {report.status !== "failed" && <Loader2 className="animate-spin gold-text mx-auto mb-3" size={26} />}
            <p className="text-lg font-light">{report.status === "pending_payment" ? "Confirming your Premium membership…" : report.status === "failed" ? "We couldn't finish this reading." : "Astra is reading both of your charts…"}</p>
            {report.status === "failed" ? (
              <p className="text-sm text-[#A7B0C0] mt-1" data-testid="synastry-failed">Please email <a href="mailto:support@astralogico.com" className="gold-text">support@astralogico.com</a> and we'll finish it for you.</p>
            ) : (
              <p className="text-sm text-[#A7B0C0] mt-1">Your full reading appears here in about a minute. You can stay on this page.</p>
            )}
          </div>
        )}
        <SynastryCharts report={report} />
        {ready && <SynastrySections report={report} />}
        {ready && !isNative && <PdfBlock report={report} onBuy={buyPdf} buying={buying} />}
      </div>
    </div>
  );
}
